import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import '@cyntox/common/styles.css';
import { Gate } from '@cyntox/common/platform';
import { initAuth } from './api/auth';
import { AppProvider } from './state/AppContext';
import { Shell } from './components/Shell';
import { Register } from './screens/Register';
import { UnitPage } from './screens/UnitPage';
import { Shops } from './screens/Shops';
import { ToDos } from './screens/ToDos';
import { Help } from './screens/Help';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function render() {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppProvider>
            <Routes>
              <Route element={<Shell />}>
                <Route index element={<Navigate to="/register" replace />} />
                {/* Each screen checks its capability at the route, so a typed URL
                    without access says so instead of showing an empty list. */}
                <Route path="/register" element={<Gate any={[['vehicles', 'view']]}><Register /></Gate>} />
                <Route path="/units/new" element={<Gate any={[['vehicles', 'edit']]}><UnitPage /></Gate>} />
                <Route path="/units/:id" element={<Gate any={[['vehicles', 'view']]}><UnitPage /></Gate>} />
                <Route path="/shops" element={<Gate any={[['vehicles', 'view']]}><Shops /></Gate>} />
                <Route path="/todo" element={<Gate any={[['vehicles', 'view']]}><ToDos /></Gate>} />
                {/* No capability: everyone who can sign in can read the guide. */}
                <Route path="/help" element={<Help />} />
                <Route path="*" element={<Navigate to="/register" replace />} />
              </Route>
            </Routes>
          </AppProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}

// Nothing renders until we know how to authenticate. With Entra that means a
// token is in hand; without it, the browser has already left for Microsoft and
// this page is being replaced.
initAuth().then(render).catch((err: Error) => {
  document.getElementById('root')!.innerHTML =
    `<div style="font:14px system-ui;padding:40px;max-width:640px">`
    + `<h2 style="margin:0 0 8px">Fleet could not start</h2>`
    + `<p style="color:#5a5852">${err.message}</p>`
    + `<p style="color:#9a9890;font-size:12px">Check that the API is running and reachable.</p>`
    + `</div>`;
});
